import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, Phone, CalendarCheck, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import Reveal from "../components/common/Reveal";
import { proxied } from "../lib/assets";
import { formatPhone } from "../lib/formUtils";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      toast.success("Message sent — I'll be in touch within 24–48 hours.");
    } catch {
      toast.error("Something didn't send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="relative py-32 lg:py-44 px-6 lg:px-10 bg-nightsky overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-55"
          src={proxied("https://customer-assets.emergentagent.com/job_escape-design-lab/artifacts/lfm9vpkq_0_Woman_White_1920x1080.mov")}
          poster="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          data-testid="contact-hero-video"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/40 to-black/70" />
        <div className="relative z-10 max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-8">
            <div className="star-sep mb-8"><span className="text-white">Contact</span></div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.98] text-white" data-testid="contact-title">
              Let's start<br />your <span className="italic text-gold">escape.</span>
            </h1>
          </Reveal>
          <Reveal className="lg:col-span-4" delay={120}>
            <p className="text-lg text-white/85 leading-relaxed font-light">
              Every unforgettable escape starts with a conversation. Whether you're ready to plan or just exploring ideas, I'm here to guide you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-24 px-6 lg:px-10 bg-ivory">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Info */}
          <Reveal className="lg:col-span-5">
            <div className="sticky top-28 space-y-10">
              <div>
                <div className="text-xs uppercase tracking-[0.28em] text-gold mb-5">How can I help?</div>
                <ul className="space-y-3 text-lg font-light text-ink">
                  <li>🌍 &nbsp;Destination ideas & travel inspiration</li>
                  <li>✈️ &nbsp;Cruise and resort bookings</li>
                  <li>🧳 &nbsp;Custom itinerary planning</li>
                  <li>🥂 &nbsp;Luxury & bucket list escapes</li>
                  <li>🧭 &nbsp;Travel questions or guidance</li>
                </ul>
                <p className="mt-6 text-sm italic text-muted">
                  No question is too small — sometimes the best escapes start with "I'm not sure where to go, but…"
                </p>
              </div>

              <div className="border-t border-hairline pt-8 space-y-5">
                <div className="flex items-start gap-4">
                  <Mail size={18} strokeWidth={1.25} className="text-gold mt-1" />
                  <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-muted">Email</div>
                    <a href="mailto:hello@luxetiniescapes.com" className="text-ink hover:text-gold" data-testid="contact-email">hello@luxetiniescapes.com</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone size={18} strokeWidth={1.25} className="text-gold mt-1" />
                  <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-muted">Phone</div>
                    <span className="text-ink" data-testid="contact-phone">By appointment</span>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CalendarCheck size={18} strokeWidth={1.25} className="text-gold mt-1" />
                  <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-muted">Consultation</div>
                    <Link to="/plan-your-escape" className="text-ink hover:text-gold" data-testid="contact-consult">Book a consultation call</Link>
                  </div>
                </div>
              </div>

              <div className="border border-ink/10 p-6">
                <div className="text-xs uppercase tracking-[0.28em] text-gold mb-2">What to expect</div>
                <ul className="text-sm text-muted space-y-2 font-light">
                  <li>• I'll review your inquiry and respond within 24–48 hours</li>
                  <li>• We'll clarify your vision, preferences, and goals</li>
                  <li>• I'll guide you to the best next steps (and service level)</li>
                </ul>
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal className="lg:col-span-7" delay={120}>
            {success ? (
              <div className="border border-gold p-12 bg-[#FBF7EC] text-center" data-testid="contact-success">
                <Check size={44} strokeWidth={1.25} className="mx-auto text-gold mb-6" />
                <h3 className="font-serif text-4xl text-ink">Message received.</h3>
                <p className="mt-4 text-muted font-light max-w-md mx-auto">
                  I'll be in touch within 24–48 hours. In the meantime, feel free to dream big.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  className="mt-8 text-[12px] uppercase tracking-[0.28em] border-b border-ink hover:text-gold hover:border-gold pb-1"
                  data-testid="contact-send-another"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-8" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Full Name *</label>
                    <input className="field-editorial" name="name" value={form.name} onChange={onChange} required data-testid="contact-input-name" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Email *</label>
                    <input className="field-editorial" type="email" name="email" value={form.email} onChange={onChange} required data-testid="contact-input-email" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Phone</label>
                    <input
                      className="field-editorial"
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
                      placeholder="555-555-5555"
                      inputMode="tel"
                      maxLength={12}
                      data-testid="contact-input-phone"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Subject</label>
                    <input className="field-editorial" name="subject" value={form.subject} onChange={onChange} data-testid="contact-input-subject" />
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Message *</label>
                  <textarea className="field-editorial" rows="5" name="message" value={form.message} onChange={onChange} required data-testid="contact-input-message" />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-3 h-14 px-10 bg-ink text-ivory hover:bg-teal transition-colors text-[12px] uppercase tracking-[0.28em] disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {submitting ? "Sending..." : "Send Message"} <ArrowUpRight size={16} strokeWidth={1.5} />
                </button>

                <p className="text-xs text-muted">By submitting, you agree to receive a reply from LuxeTini Escapes.</p>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
